import { GradientScrollDiv } from '@components/util/GradientScrollDiv';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { userStore } from '@stores/User';
import { observer } from 'mobx-react';
import css from './LibraryList.module.scss';
import { LibraryListRow } from './LibraryListRow';
import { renderLibrarySections } from './renderLibrarySections';

interface LibraryListProps {
  onMobileMenuClick: (libraryId: string, libraryName: string, thumbnailBlobUrl?: string) => void;
  hiddenLibraryId: string | null;
  onNewLibrary: () => void;
}

/**
 * **Mobile** list view — the desktop equivalent is `LibrariesTable`, a data grid.
 * Reads the same `librarySections` as the grid and the table, so sort, filter, pinned-first
 * ordering *and* the section grouping all carry across a view switch with no migration.
 * Pinning itself is reached from the row's ⋯ drawer, since the card's pin button is a
 * hover affordance.
 *
 * Not virtualized, deliberately: libraries number in the tens, and virtualizing
 * that many uniform rows buys nothing while adding scroll-restoration bugs.
 */
export const LibraryList = observer(
  ({ onMobileMenuClick, hiddenLibraryId, onNewLibrary }: LibraryListProps) => {
    const libraryManager = useLibraryManager();
    // Only verified users can create libraries (mirrors the nav "New library" button).
    const canCreate = !!userStore.data?.isVerified;

    return (
      <GradientScrollDiv className={css.container}>
        <div className={css.inner}>
          {renderLibrarySections(libraryManager.librarySections, {
            labelClassName: css.sectionLabel,
            // `onMobileMenuClick` is handed straight down rather than wrapped per row: the row
            // already has its library, so a closure here would give every row a fresh prop and
            // stop `LibraryListRow`'s `observer` memo from skipping the untouched ones — which
            // matters most on this surface, since its ⋯ drawer is mobile's only route to pinning.
            renderItem: (library) => (
              <LibraryListRow
                key={library.libraryId}
                library={library}
                isHidden={hiddenLibraryId === library.libraryId}
                onMobileMenuClick={onMobileMenuClick}
              />
            ),
          })}
          {canCreate && (
            <button
              type="button"
              className={css.newLibraryRow}
              onClick={onNewLibrary}
              aria-label="Create a new library"
            >
              <AddRoundedIcon className={css.newLibraryRowIcon} />
              <span>New library</span>
            </button>
          )}
        </div>
      </GradientScrollDiv>
    );
  },
);
